import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, Copy, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Toggle this to true when diagnosing bar-visibility issues. Kept in
// the source (rather than a runtime env flag) so it's easy to grep
// and enable temporarily. Was ON during the 2026-07-30 investigation.
const DEBUG = false;
const log = (...a) => { if (DEBUG) { try { console.log('[PromoBar]', ...a); } catch {} } };

/**
 * Site-wide promotion announcement bar.
 *
 * Selection algorithm (per 2026-07-30 spec):
 *   1. Fetch all active + publicly-visible promotions.
 *   2. Prefer any promo with `display.featured=true` (highest priority wins).
 *   3. Otherwise take the highest-priority active promo.
 *   4. `display.show_in_announcement_bar` is honoured when present, but
 *      NOT required — the historical bug was that no promo had that
 *      flag toggled on, so the endpoint returned `[]` and the bar
 *      silently disappeared. The spec explicitly says "select the
 *      promotion marked as featured or primary … do not hide the
 *      banner merely because multiple promotions are active".
 *
 * Dismissal:
 *   - Keyed by `${promo.id}:${promo.updated_at}` so editing the promo
 *     (new expiry, new headline, new code) automatically re-surfaces
 *     the bar. Switching from SAVE25 → SAVE15 also re-surfaces it.
 *   - Stored in localStorage so it survives refresh but clears when
 *     the promo changes.
 */
const DISMISS_KEY = 'rcm_promo_bar_dismissed_v2';

const pickBestPromo = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  const active = list.filter(p => p && p.status === 'active');
  if (active.length === 0) return null;
  // A live promotion always beats an early-announcement teaser.
  const live = active.filter(p => !p.teaser);
  const candidates = live.length > 0 ? live : active;
  const featured = candidates.filter(p => p?.display?.featured);
  const pool = featured.length > 0 ? featured : candidates;
  // Highest display_priority wins; tie-break by earliest created_at so
  // the ordering is stable across page loads.
  return [...pool].sort((a, b) => {
    const pa = a?.display?.display_priority ?? 0;
    const pb = b?.display?.display_priority ?? 0;
    if (pb !== pa) return pb - pa;
    return (a?.created_at || '').localeCompare(b?.created_at || '');
  })[0];
};

// Build-time promotions snapshot (prerender.js) → first paint shows the bar without
// a layout shift. Expired entries are dropped; the live fetch below replaces it.
const readPromoSnapshot = () => {
  try {
    const el = typeof document !== 'undefined' && document.getElementById('__RCM_PROMOS__');
    if (!el) return null;
    const now = Date.now();
    const list = JSON.parse(el.textContent).filter((p) => {
      const exp = p?.schedule?.expires_at;
      if (p?.teaser && p?.reveals_at && Date.parse(p.reveals_at) <= now) return false;
      return !exp || Date.parse(exp) > now;
    });
    return pickBestPromo(list);
  } catch {
    return null;
  }
};

const dismissKeyFor = (promo) => {
  if (!promo?.id) return null;
  // Fingerprint every field that would let a customer meaningfully
  // re-see the banner: promo id, code, expiration, and last-edit
  // timestamp. If ANY of those change, the stored key no longer
  // matches and the bar re-appears automatically. This is stronger
  // than the previous `${id}:${updated_at}` scheme — SAVE15 got a
  // new `expires_at` on 2026-07-30 but the row's `updated_at` didn't
  // move (bulk data patch), so dismissed customers never saw it come
  // back. Including `code + expires_at` closes that gap.
  const parts = [
    promo.id,
    promo.code || '',
    promo.updated_at || '',
    promo.schedule?.expires_at || '',
  ];
  return parts.join('|');
};

const PromotionAnnouncementBar = () => {
  const [promo, setPromo] = useState(readPromoSnapshot);
  const [dismissed, setDismissed] = useState(() => {
    try { return Boolean(promo) && localStorage.getItem(DISMISS_KEY) === dismissKeyFor(promo); } catch { return false; }
  });
  const [copied, setCopied] = useState(false);
  const barRef = useRef(null);

  // Fetch + re-select. Runs on mount, on window focus (so returning to
  // the tab after the admin edits the promo picks up changes), and
  // whenever the age-verification modal closes (custom event below).
  const fetchPromo = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/promotions`);
      log('fetch status', res.status);
      if (!res.ok) return;
      const list = await res.json();
      log('active promotions returned:', Array.isArray(list) ? list.length : 'n/a',
        Array.isArray(list) ? list.map(p => ({
          code: p.code,
          id: p.id,
          featured: p?.display?.featured,
          priority: p?.display?.display_priority,
          public_vis: p?.display?.public_visibility,
        })) : list);
      const chosen = pickBestPromo(list);
      log('featured promotion selected:', chosen ? { id: chosen.id, code: chosen.code, updated_at: chosen.updated_at } : null);
      setPromo(chosen);
    } catch (e) {
      log('fetch error:', e?.message);
    }
  }, []);

  useEffect(() => {
    fetchPromo();
    // Re-check when user returns to tab.
    const onFocus = () => fetchPromo();
    window.addEventListener('focus', onFocus);
    // Custom event fired by AgeVerificationModal on accept — see below.
    const onAgeAccepted = () => {
      log('age-verification accepted → recalculating banner');
      fetchPromo();
      // Force a reflow so any sticky-offset calculations run again.
      requestAnimationFrame(() => {
        if (barRef.current) {
          const h = barRef.current.getBoundingClientRect().height;
          document.documentElement.style.setProperty('--promo-bar-height', `${h}px`);
        }
      });
    };
    window.addEventListener('rcm:age-verified', onAgeAccepted);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('rcm:age-verified', onAgeAccepted);
    };
  }, [fetchPromo]);

  // Whenever the selected promo changes, evaluate dismissal (keyed by
  // promo id + updated_at) and expose the bar height as a CSS var so
  // any sticky/fixed header can offset itself if needed.
  useEffect(() => {
    if (!promo) {
      setDismissed(false);
      document.documentElement.style.setProperty('--promo-bar-height', '0px');
      return;
    }
    const key = dismissKeyFor(promo);
    let val = null;
    try { val = localStorage.getItem(DISMISS_KEY); } catch {}
    log('dismissal key checked:', key, '| stored value:', val, '| dismissed?', val === key);
    setDismissed(val === key);
    // Publish bar height for downstream layout (header can subtract it).
    requestAnimationFrame(() => {
      if (barRef.current) {
        const h = barRef.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--promo-bar-height', `${h}px`);
        log('bar height set to', h, 'px');
      }
    });
  }, [promo]);

  const visibleFinal = useMemo(() => Boolean(promo && !dismissed), [promo, dismissed]);

  useEffect(() => {
    log('final banner visibility state:', visibleFinal);
  }, [visibleFinal]);

  // When a promo is available but the customer previously dismissed it,
  // render a small "Show promo" chip in the bottom-left so they always
  // have an escape hatch (per 2026-07-30 spec). This never persists
  // hidden state — clicking the chip clears the dismissal and reveals
  // the full banner immediately.
  if (!promo) return null;
  if (dismissed) {
    return (
      <button
        type="button"
        data-testid="promo-bar-restore"
        onClick={() => {
          try { localStorage.removeItem(DISMISS_KEY); } catch {}
          setDismissed(false);
        }}
        aria-label={`Show ${promo.code || 'current'} promotion banner`}
        title="Show the promotion banner you dismissed"
        style={{
          position: 'fixed',
          left: '16px',
          bottom: '16px',
          zIndex: 130,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          background: 'linear-gradient(90deg, #071A33 0%, #0057B8 100%)',
          color: '#FFFFFF',
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: '999px',
          boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
          letterSpacing: '0.02em',
        }}
      >
        <Sparkles size={14} style={{ color: '#00AEEF' }} aria-hidden="true" />
        Show promo{promo.code ? ` · ${promo.code}` : ''}
      </button>
    );
  }

  const code = promo.code || '';
  const headline = promo.content?.public_headline || 'Limited-Time Offer';
  const shortDesc = promo.content?.public_short_description || '';

  const handleDismiss = () => {
    const key = dismissKeyFor(promo);
    if (key) {
      try { localStorage.setItem(DISMISS_KEY, key); } catch {}
    }
    setDismissed(true);
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div
      ref={barRef}
      data-testid="promo-announcement-bar"
      data-promo-id={promo.id}
      data-promo-code={promo.code}
      role="region"
      aria-label="Site-wide promotion"
      style={{
        // Deep navy → electric blue gradient (approved palette only)
        background: 'linear-gradient(90deg, #071A33 0%, #0057B8 55%, #00AEEF 100%)',
        color: '#FFFFFF',
        padding: '10px 16px',
        position: 'relative',
        // Must sit ABOVE the sticky header (which is z-index 100 in
        // App.css). Otherwise as soon as the user scrolls a single
        // pixel, the header would visually stack over the banner —
        // the exact "banner in HTML but not visible" symptom reported
        // on 2026-07-30. Pinning to 120 keeps it above every existing
        // sticky/fixed layer (age-modal is 9999 which is above by
        // design so it dims correctly).
        zIndex: 120,
        borderBottom: '1px solid rgba(199, 211, 223, 0.25)',
      }}
    >
      <div
        className="container mx-auto"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '14px',
          flexWrap: 'wrap',
          fontSize: '13px',
          lineHeight: 1.4,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 600,
          }}
        >
          <Sparkles size={16} style={{ color: '#00AEEF', flexShrink: 0 }} aria-hidden="true" />
          <span data-testid="promo-bar-headline">{headline}</span>
        </span>

        {shortDesc && (
          <span
            data-testid="promo-bar-desc"
            style={{ color: 'rgba(255,255,255,0.85)', display: 'none' }}
            className="promo-bar-desc-md"
          >
            {shortDesc}
          </span>
        )}

        {promo.teaser && promo.reveals_at && (
          <span
            data-testid="promo-bar-reveal-date"
            style={{ background: 'rgba(255,255,255,0.14)', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}
          >
            Code revealed {new Date(promo.reveals_at).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} CT
          </span>
        )}

        {code && (
          <button
            type="button"
            onClick={handleCopy}
            data-testid="promo-bar-copy-code"
            aria-label={`Copy promo code ${code}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#FFFFFF',
              color: '#071A33',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.05em',
            }}
          >
            <span>{code}</span>
            {copied ? (
              <>
                <Check size={13} aria-hidden="true" />
                <span data-testid="promo-bar-copied-label">Copied</span>
              </>
            ) : (
              <Copy size={13} aria-hidden="true" />
            )}
          </button>
        )}

        <Link
          to="/promotions"
          data-testid="promo-bar-view-details"
          style={{
            color: '#FFFFFF',
            textDecoration: 'underline',
            textDecorationColor: 'rgba(255,255,255,0.55)',
            textUnderlineOffset: '3px',
            fontWeight: 600,
          }}
        >
          View details
        </Link>

        <button
          type="button"
          onClick={handleDismiss}
          data-testid="promo-bar-dismiss"
          aria-label="Dismiss promotion bar"
          title="Dismiss this promotion — the bar will reappear when the promotion changes."
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            color: '#FFFFFF',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            opacity: 0.75,
            display: 'flex',
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      {/* Responsive: only reveal the short description on ≥ 768px so the
          bar doesn't wrap awkwardly on mobile. */}
      <style>{`
        @media (min-width: 768px) {
          [data-testid="promo-announcement-bar"] .promo-bar-desc-md {
            display: inline !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PromotionAnnouncementBar;
